/**
 * ApprovingForSwitcher
 *
 * Shows a searchable "Approving for:" dropdown in the Approvals header.
 * Only rendered when the organisation has "Approve on behalf of others" enabled in
 * Settings → General → On Behalf Of — the parent is responsible for the gate.
 * When an approver other than "Myself" is selected, an amber info banner is
 * shown below the header indicating elevated-action context.
 */

import { useRef, useState } from 'react';
import { ChevronDown, Users, X } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ApprovingForApprover {
  id: string;
  name: string;
}

export type ApprovingForSelection =
  | { type: 'myself'; id: 'myself'; name: string }
  | { type: 'other'; id: string; name: string };

interface ApprovingForSwitcherProps {
  /** The name of the currently logged-in user, e.g. "Michael Williams" */
  currentUserName: string;
  /** List of approvers this user is permitted to act for */
  permittedApprovers: ApprovingForApprover[];
  /** Currently selected approver (controlled) */
  selected: ApprovingForSelection;
  /** Called when the user picks a different approver */
  onChange: (selection: ApprovingForSelection) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ApprovingForSwitcher({
  currentUserName,
  permittedApprovers,
  selected,
  onChange,
}: ApprovingForSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const myselfOption: ApprovingForSelection = {
    type: 'myself',
    id: 'myself',
    name: currentUserName,
  };

  const allOptions: ApprovingForSelection[] = [
    myselfOption,
    ...permittedApprovers.map(a => ({ type: 'other' as const, id: a.id, name: a.name })),
  ];

  const filtered = search
    ? allOptions.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : allOptions;

  const isOther = selected.type === 'other';

  const handleSelect = (option: ApprovingForSelection) => {
    onChange(option);
    setOpen(false);
    setSearch('');
  };

  const displayLabel =
    selected.type === 'myself' ? `Myself (${selected.name})` : selected.name;

  return (
    <div className="flex flex-col gap-0">
      {/* ── Dropdown control ── */}
      <div className="flex items-center gap-3" ref={dropdownRef}>
        <span className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground whitespace-nowrap">
          <Users className="h-4 w-4" />
          Approving for:
        </span>

        <div className="relative">
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen(v => !v)}
            className={[
              'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium',
              'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isOther
                ? 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100'
                : 'bg-background border-border hover:border-primary/50 text-foreground',
            ].join(' ')}
          >
            <span className="max-w-[220px] truncate">{displayLabel}</span>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
            />
          </button>

          {/* ── Dropdown panel ── */}
          {open && (
            <>
              {/* Backdrop to close on outside click */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => { setOpen(false); setSearch(''); }}
              />
              <div className="absolute left-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-xl border border-border bg-popover shadow-xl">
                {/* Search */}
                <div className="border-b border-border px-3 py-2">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search approver…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                </div>

                {/* Options */}
                <ul role="listbox" className="max-h-60 overflow-y-auto py-1">
                  {filtered.length === 0 && (
                    <li className="px-4 py-3 text-sm text-muted-foreground">No results found</li>
                  )}
                  {filtered.map(option => {
                    const isSelected = option.id === selected.id;
                    return (
                      <li key={option.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => handleSelect(option)}
                          className={[
                            'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                            isSelected
                              ? 'bg-primary/10 text-primary font-semibold'
                              : 'hover:bg-muted text-foreground',
                          ].join(' ')}
                        >
                          {option.type === 'myself' ? (
                            <>
                              <span className="flex-1">Myself ({option.name})</span>
                              {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                            </>
                          ) : (
                            <>
                              <span className="flex-1">{option.name}</span>
                              {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                            </>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
}

// ─── Standalone banner ────────────────────────────────────────────────────────

interface ApprovingForBannerProps {
  selected: ApprovingForSelection;
  onBackToMyself: () => void;
}

/**
 * Full-width amber banner rendered outside the header row,
 * between the page header and the KPI cards.
 */
export function ApprovingForBanner({ selected, onBackToMyself }: ApprovingForBannerProps) {
  if (selected.type !== 'other') return null;

  return (
    <div className="flex w-full items-center justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5">
      <p className="text-sm text-amber-800">
        <span className="font-semibold">You are approving on behalf of {selected.name}.</span>
        {' '}All actions are recorded against your account.
      </p>
      <button
        type="button"
        onClick={onBackToMyself}
        className="flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-amber-700 underline-offset-2 hover:text-amber-900 hover:underline"
      >
        <X className="h-3.5 w-3.5" />
        Back to my approvals
      </button>
    </div>
  );
}

// ─── Mock data ───────────────────────────────────────────────────────────────

/** Approvers the logged-in user is permitted to act for (mock). */
export const MOCK_PERMITTED_APPROVERS: ApprovingForApprover[] = [
  { id: 'john-davis', name: 'John Davis' },
  { id: 'matt-brown', name: 'Matt Brown' },
];
